import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  Paper,
  Typography,
  Chip,
} from "@mui/material";
import {
  Add,
  Search,
  FilterList,
  MoreVert,
  Visibility,
  Edit,
  Delete,
} from "@mui/icons-material";
import AddSettlement from "./AddSettlement";

function Settlement() {
  // State for data and UI controls
  const [settlements, setSettlements] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSettlementId, setSelectedSettlementId] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);

  // Handle menu open/close
  const handleMenuClick = (event, settlementId) => {
    setAnchorEl(event.currentTarget);
    setSelectedSettlementId(settlementId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSettlementId(null);
  };

  // Action handlers
  const handleView = () => {
    console.log("View settlement:", selectedSettlementId);
    handleMenuClose();
  };

  const handleEdit = () => {
    console.log("Edit settlement:", selectedSettlementId);
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log("Delete settlement:", selectedSettlementId);
    handleMenuClose();
  };

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleAddSettlementClick = () => {
    setOpenAddDialog(true);
  };

  const handleAddDialogClose = () => {
    setOpenAddDialog(false);
  };

  // Fetch settlement data on component mount
  useEffect(() => {
    const fetchSettlements = async () => {
      setLoading(true);
      try {
        // In a real app, this would be an API call
        const response = await fetch('https://dummyjson.com/users?limit=20');
        const data = await response.json();
        
        // Format data for our settlement table
        const formattedData = data.users.map(user => ({
          id: `STL-${user.id.toString().padStart(4, '0')}`,
          amount: `$${(Math.random() * 10000).toFixed(2)}`,
          name: `${user.firstName} ${user.lastName}`,
          time: (() => {
            const d = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000);
            const dd = String(d.getDate()).padStart(2, "0");
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            return `${dd}-${mm}-${d.getFullYear()}`;
          })(),
          status: Math.random() > 0.5 ? "completed" : "pending"
        }));
        
        setSettlements(formattedData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettlements();
  }, []);

  // Filter settlements based on search query
  const filteredSettlements = settlements.filter(
    (settlement) =>
      settlement.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      settlement.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayedSettlements = filteredSettlements.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: "#212121" }}>
          Settlement Management
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" mb={3}>
        <TextField
          placeholder="Search settlements..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            width: 300,
            bgcolor: "#ffffff",
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#e0e0e0" },
              "&:hover fieldset": { borderColor: "#bdbdbd" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{
              borderColor: "#e0e0e0",
              color: "#424242",
              "&:hover": {
                borderColor: "#9e9e9e",
                bgcolor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Filters
          </Button>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddSettlementClick}
            sx={{
              bgcolor: "#4285F4",
              color: "white",
              "&:hover": {
                bgcolor: "#3367D6",
              },
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Add Settlement
          </Button>
        </Box>
      </Box>

      <Card sx={{ boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)", bgcolor: "#ffffff" }}>
        <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "#ffffff" }}>
          <Table>
            <TableHead sx={{ bgcolor: "#f9fafc" }}>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  Settlement ID
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  User Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  Time
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ffffff" }}>
              {loading ? (
                <TableRow sx={{ bgcolor: "#ffffff" }}>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: "#212121" }}>
                    Loading settlements...
                  </TableCell>
                </TableRow>
              ) : displayedSettlements.length === 0 ? (
                <TableRow sx={{ bgcolor: "#ffffff" }}>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: "#212121" }}>
                    No settlements found
                  </TableCell>
                </TableRow>
              ) : (
                displayedSettlements.map((settlement) => (
                  <TableRow
                    key={settlement.id}
                    sx={{
                      bgcolor: "#ffffff",
                      "&:hover": { bgcolor: "#f5f5f5" },
                      cursor: "pointer",
                    }}
                  >
                    <TableCell sx={{ color: "#212121" }}>
                      {settlement.id}
                    </TableCell>
                    <TableCell sx={{ color: "#212121", fontWeight: 500 }}>
                      {settlement.amount}
                    </TableCell>
                    <TableCell sx={{ color: "#212121" }}>
                      {settlement.name}
                    </TableCell>
                    <TableCell sx={{ color: "#212121" }}>
                      {settlement.time}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={settlement.status} 
                        size="small"
                        sx={{
                          bgcolor: settlement.status === "completed" ? "#e8f5e9" : "#fff8e1",
                          color: settlement.status === "completed" ? "#2e7d32" : "#f57c00",
                          fontWeight: 500,
                          textTransform: "capitalize"
                        }} 
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, settlement.id)}
                        sx={{ color: "#757575" }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredSettlements.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              bgcolor: "#ffffff",
              color: "#212121",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  color: "#212121",
                },
              "& .MuiTablePagination-select": {
                color: "#212121",
              },
              "& .MuiTablePagination-selectIcon": {
                color: "#757575",
              },
              "& .MuiIconButton-root": {
                color: "#757575",
              },
            }}
          />
        </TableContainer>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
            minWidth: 120,
            bgcolor: "#ffffff",
          },
        }}
      >
        <MenuItem onClick={handleView} sx={{ color: "#212121" }}>
          <Visibility fontSize="small" sx={{ mr: 1, color: "#4285F4" }} /> View
        </MenuItem>
        <MenuItem onClick={handleEdit} sx={{ color: "#212121" }}>
          <Edit fontSize="small" sx={{ mr: 1, color: "#4285F4" }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "#212121" }}>
          <Delete fontSize="small" sx={{ mr: 1, color: "#f44336" }} /> Delete
        </MenuItem>
      </Menu>

      {/* Add Settlement Dialog */}
      <AddSettlement open={openAddDialog} onClose={handleAddDialogClose} />
    </Box>
  );
}

export default Settlement;